import { createContext, useContext, useMemo, type PropsWithChildren } from "react";
import { useAsyncData } from "../../hooks/useAsyncData";
import { getTaxonomyNodes } from "../../lib/expert_insights_api";
import { TaxonomyView } from "../../lib/taxonomyView";
import { useUserContext } from "../UserContext";

const EMPTY_TAXONOMY = new TaxonomyView([]);

/** The subject skeleton, empty until it has loaded (and for a visitor who is not signed in). */
export const TaxonomyContext = createContext<TaxonomyView>(EMPTY_TAXONOMY);

function LoadTaxonomy({ children }: PropsWithChildren) {
  const { data } = useAsyncData(getTaxonomyNodes);
  const taxonomy = useMemo(() => (data ? new TaxonomyView(data) : EMPTY_TAXONOMY), [data]);
  return <TaxonomyContext.Provider value={taxonomy}>{children}</TaxonomyContext.Provider>;
}

/** Loads the subject skeleton once a signed-in user is present and shares it with the tree below. */
export function TaxonomyProvider({ children }: PropsWithChildren) {
  const { session } = useUserContext();
  const user = session?.user;
  if (!user || user.is_anonymous) return <>{children}</>;
  return <LoadTaxonomy key={user.id}>{children}</LoadTaxonomy>;
}

/** The subject skeleton: domains and fields. Empty while it loads, so render for that. */
export function useTaxonomy(): TaxonomyView {
  return useContext(TaxonomyContext);
}
